import { lazy } from 'react';
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import useAuth from '../useAuth';
import { ROLE, ROUTE } from '../../types';

// GLOBAL IMPORT
const DebugPage = lazy(() => import('../../pages/DebugPage/DebugPage'));
const FatalErrorPage = lazy(() => import('../../pages/FatalErrorPage/FatalErrorPage'));
const NotFoundPage = lazy(() => import('../../pages/NotFoundPage/NotFoundPage'));

// AUTH IMPORT
const LoginPage = lazy(() => import('../../pages/LoginPage/LoginPage'));
const LoginPageCallBack = lazy(() => import('../../pages/LoginPage/LoginCallback'));
const LogoutPage = lazy(() => import('../../pages/LogoutPage/LogoutPage'));
const RequireAuth = lazy(() => import('../../libs/routing/components/RequireAuth/RequireAuth'));
const SignUpPage = lazy(() => import('../../pages/SignUpPage/SignUpPage'));
const SignupProfilePage = lazy(() => import('../../pages/SignupProfilePage/SignupProfilePage'));
const SignUpToAttestraConnectPage = lazy(
  () => import('../../pages/SignUpToAttestraConnectPage/SignUpToAttestraConnectPage')
);
const UserProfilePage = lazy(() => import('../../pages/UserProfilePage/UserProfilePage'));

// HOME IMPORT
const HomePage = lazy(() => import('../../pages/HomePage/HomePage'));
const HomeProductionView = lazy(() => import('../../pages/HomePage/view/HomeProductionView'));

// DASHBOARD IMPORT
const Dashboard = lazy(() => import('../../pages/DashboardPage/DashboardPage'));
const DashboardNavigate = lazy(
  () => import('../../libs/routing/components/DashboardNavigate/DashboardNavigate')
);
const DashboardAdminPermitholder = lazy(
  () => import('../../pages/DashboardAdminPermitholderPage/DashboardAdminPermitholderPage')
);
const DashboardAdminProducer = lazy(
  () => import('../../pages/DashboardAdminProducerPage/DashboardAdminProducerPage')
);
const DashboardAdminVeterinarian = lazy(
  () => import('../../pages/DashboardAdminVeterinarianPage/DashboardAdminVeterinarianPage')
);
const DashboardOrganization = lazy(
  () => import('../../pages/DashboardOrganizationPage/DashboardOrganizationPage')
);
const DashboardProducer = lazy(
  () => import('../../pages/DashboardProducerPage/DashboardProducerPage')
);
const DashboardVeterinarian = lazy(
  () => import('../../pages/DashboardVeterinarianPage/DashboardVeterinarianPage')
);
const DashboardVeterinarianProducer = lazy(
  () => import('../../pages/DashboardVeterinarianProducerPage/DashboardVeterinarianProducerPage')
);
const DashboardVeterinarianPurchaseSummaryPage = lazy(
  () =>
    import(
      '../../pages/DashboardVeterinarianProducerPurchaseSummaryPage/DashboardVeterinarianProducerPurchaseSummaryPage'
    )
);
const VeterinarianPrescriptionsSummaryPage = lazy(
  () =>
    import('../../pages/VeterinarianPrescriptionsSummaryPage/VeterinarianPrescriptionsSummaryPage')
);
const ReportPage = lazy(() => import('../../modules/report/views/ReportView/ReportView'));
const RndDashboardOrganization = lazy(
  () => import('../../pages/RndDashboardOrganizationPage/RndDashboardOrganizationPage')
);
const RndDashboardPermitholder = lazy(
  () => import('../../pages/RndDashboardPermitholderPage/RndDashboardPermitholderPage')
);
const RndDashboardProducer = lazy(
  () => import('../../pages/RndDashboardProducerPage/RndDashboardProducerPage')
);
const RndDashboardVeterinarian = lazy(
  () => import('../../pages/RndDashboardVeterinarianPage/RndDashboardVeterinarianPage')
);
const UserDashboardOrganization = lazy(
  () => import('../../pages/UserDashboardOrganizationPage/UserDashboardOrganizationPage')
);
const UserDashboardPermitholder = lazy(
  () => import('../../pages/UserDashboardPermitholderPage/UserDashboardPermitholderPage')
);
const UserDashboardProducer = lazy(
  () => import('../../pages/UserDashboardProducerPage/UserDashboardProducerPage')
);
const UserDashboardVeterinarian = lazy(
  () => import('../../pages/UserDashboardVeterinarianPage/UserDashboardVeterinarianPage')
);

// ENTITY IMPORT
const AccessApiKeyListPage = lazy(
  () => import('../../pages/AccessApiKeyListPage/AccessApiKeyListPage')
);
const ActiveIngredientListPage = lazy(
  () => import('../../pages/ActiveIngredientListPage/ActiveIngredientListPage')
);
const AnimalCategoryListPage = lazy(
  () => import('../../pages/AnimalCategoryListPage/AnimalCategoryListPage')
);
const DeclarationCreatePage = lazy(
  () => import('../../pages/DeclarationCreatePage/DeclarationCreatePage')
);
const DeclarationCreateFromTemplateLotPage = lazy(
  () =>
    import('../../pages/DeclarationCreateFromTemplateLotPage/DeclarationCreateFromTemplateLotPage')
);
const DeclarationDuplicatePage = lazy(
  () => import('../../pages/DeclarationDuplicatePage/DeclarationDuplicatePage')
);
const DeclarationEditPage = lazy(
  () => import('../../pages/DeclarationEditPage/DeclarationEditPage')
);
const DeclarationListPage = lazy(() => import('../../pages/DeclarationListPage'));
const DeclarationMultistepCreatePage = lazy(
  () => import('../../pages/DeclarationMultistepCreatePage/DeclarationMultistepCreatePage')
);
const DeclarationMultistepDuplicatePage = lazy(
  () => import('../../pages/DeclarationMultistepDuplicatePage/DeclarationMultistepDuplicatePage')
);
const DeclarationSiteUpdateListPage = lazy(
  () => import('../../pages/DeclarationSiteUpdateListPage')
);
const DeclarationTemplateListPage = lazy(() => import('../../pages/DeclarationTemplateListPage'));
const DeclarationTemplateLotListPage = lazy(
  () => import('../../pages/DeclarationTemplateLotListPage')
);
const FinalAddressListPage = lazy(
  () => import('../../pages/FinalAddressListPage/FinalAddressListPage')
);
const IngredientFamilyList = lazy(
  () => import('../../modules/ingredient-family/components/List/IngredientFamilyList')
);
const MedicationListPage = lazy(() => import('../../pages/MedicationListPage/MedicationListPage'));
const MedicationOverviewPage = lazy(
  () => import('../../pages/MedicationOverviewPage/MedicationOverviewPage')
);
const MedicationTypeListPage = lazy(
  () => import('../../pages/MedicationTypeListPage/MedicationTypeListPage')
);
const OmvqEstablishmentListPage = lazy(
  () => import('../../pages/OmvqEstablishmentListPage/OmvqEstablishmentListPage')
);
const OmvqEstablishmentCodeListPage = lazy(
  () => import('../../pages/OmvqEstablishmentCodeListPage/OmvqEstablishmentCodeListPage')
);
const OmvqVeterinarianListPage = lazy(
  () => import('../../pages/OmvqVeterinarianListPage/OmvqVeterinarianListPage')
);
const OrganizationListPage = lazy(
  () => import('../../pages/OrganizationListPage/OrganizationListPage')
);
const PendingDeclarationEdit = lazy(
  () => import('../../pages/PendingDeclarationEditPage/PendingDeclarationEditPage')
);
const PendingDeclarationPage = lazy(
  () => import('../../pages/PendingDeclarationPage/PendingDeclarationPage')
);
const PermitList = lazy(() => import('../../modules/permit/components/view/PermitList'));
const PermitCodeAdd = lazy(() => import('../../pages/PermitCode/Add'));
const PermitCodeEdit = lazy(() => import('../../pages/PermitCode/Edit'));
const PermitCodeLister = lazy(() => import('../../pages/PermitCode/List'));
const PermitView = lazy(() => import('../../modules/permit/components/view/PermitView'));
const PermitHolderDocPage = lazy(
  () => import('../../pages/DocPages/PermitHolderDocPage/PermitHolderDocPage')
);
const ProducerAccessRequestList = lazy(
  () => import('../../modules/producer-request-access/components/view/RequestList')
);
const ProducerRequestAccessFormPage = lazy(
  () => import('../../pages/ProducerRequestAccessFormPage/ProducerRequestAccessFormPage')
);
const ProducerRequestUserAccessList = lazy(
  () => import('../../modules/producer-request-access/components/view/UserAccessList')
);
const ProducerRequestUserAccessDetailList = lazy(
  () => import('../../modules/producer-request-access/components/view/UserAccessDetailList')
);
const ProductListPage = lazy(() => import('../../pages/ProductListPage/ProductListPage'));
const ProductTypeLister = lazy(() => import('../../pages/Type/ProductType/List'));
const ProductionStagesListPage = lazy(
  () => import('../../pages/ProductionStageListPage/ProductionStageListPage')
);
const ProductionStagesGroupListPage = lazy(
  () => import('../../pages/ProductionStageGroupListPage/ProductionStageGroupListPage')
);
const RegulationDateLimitListPage = lazy(
  () => import('../../modules/regulation-date-limit/components/view/RegulationDateLimitList')
);
const RouteOfAdministrationList = lazy(
  () => import('../../modules/route-of-administration/components/List/RouteOfAdministrationList')
);
const SpeciesListPage = lazy(() => import('../../pages/SpeciesListPage/SpeciesListPage'));
const UserApiKeyAdminListPage = lazy(
  () => import('../../pages/UserApiKeyAdminListPage/UserApiKeyAdminListPage')
);
const UserApiKeyLister = lazy(() => import('../../pages/UserApiKeyListPage/UserApiKeyListPage'));
const UserListPage = lazy(() => import('../../pages/UserListPage/UserListPage'));
const UseTermListPage = lazy(
  () => import('../../modules/settings/views/UseTermListView/UseTermListView')
);
const VetDeclarationListPage = lazy(() => import('../../pages/VetDeclarationListPage'));
const VetDocPage = lazy(() => import('../../pages/DocPages/VetDocPage/VetDocPage'));

const SystemPageAdmin = lazy(() => import('../../pages/SystemAdminPage/SystemAdminPage'));

const ProducerPurchaseSummaryPage = lazy(
  () => import('../../pages/ProducerPurchaseSummaryPage/ProducerPurchaseSummaryPage')
);

// AUTH ROUTES
const userAccountRoutes = {
  path: 'users',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <UserListPage /> },
  ],
};

const userProfileRoute = {
  path: 'profile',
  element: (
    <RequireAuth>
      <UserProfilePage />
    </RequireAuth>
  ),
};

// GLOBAL ROUTES
const debugRoute = {
  path: 'debug',
  element: (
    <RequireAuth>
      <DebugPage />
    </RequireAuth>
  ),
};

const debugHomeProdRoute = {
  path: 'debug-home-prod',
  element: <HomeProductionView />,
};

const docsRoutes = {
  path: 'docs',
  children: [
    { children: [{ path: 'api/permit-holder', element: <PermitHolderDocPage /> }] },
    { children: [{ path: 'api/vet', element: <VetDocPage /> }] },
  ],
};

const mainRoutes = {
  path: '/',
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: '/', element: <Navigate to="/dashboard" /> },
    { path: 'signup', element: <SignupProfilePage /> },
    { path: 'logout', element: <LogoutPage /> },
    { path: 'logout_callback', element: <Navigate to="/login" /> },
    { path: 'login', element: <Navigate to="/dashboard" /> },
    { path: 'login_callback', element: <LoginPageCallBack /> },
    { path: '404', element: <NotFoundPage /> },
    { path: '500', element: <FatalErrorPage /> },
  ],
};

const userAccountCreationRoutes = {
  path: '/',
  children: [{ path: '/signup/:profile', element: <SignUpPage /> }],
};

const roleSSOLoggedInNoUserRoutes = {
  path: '/',
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: '/home', element: <HomePage /> },
    { path: '/login', element: <LoginPage /> },
    { path: '/login_callback', element: <LoginPageCallBack /> },
    { path: '/logout_callback', element: <Navigate to="/login" /> },
    { path: '/signup_attestra_connect', element: <SignUpToAttestraConnectPage /> },
    { path: 'logout', element: <LogoutPage /> },
    { path: 'login', element: <Navigate to="/dashboard" /> },
    { path: '404', element: <NotFoundPage /> },
    { path: '500', element: <FatalErrorPage /> },
    userAccountCreationRoutes,
    debugHomeProdRoute,
    docsRoutes,
  ],
};

// DASHBOARD ROUTES
const dashboardRoutes = {
  path: 'dashboard',
  element: <DashboardNavigate />,
  children: [
    { path: '', element: <Navigate to="/dashboard" /> },
    {
      element: (
        <RequireAuth
          allowedRoles={[
            ROLE.ROLE_ADMIN,
            ROLE.ROLE_MAPAQ,
            ROLE.ROLE_MAPAQ_RO,
            ROLE.ROLE_SUPPORT,
            ROLE.ROLE_SUPPORT_RO,
          ]}
        >
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'admin_permit', element: <DashboardAdminPermitholder /> }],
    },
    {
      element: (
        <RequireAuth
          allowedRoles={[
            ROLE.ROLE_ADMIN,
            ROLE.ROLE_MAPAQ,
            ROLE.ROLE_MAPAQ_RO,
            ROLE.ROLE_SUPPORT,
            ROLE.ROLE_SUPPORT_RO,
          ]}
        >
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'admin_producer', element: <DashboardAdminProducer /> }],
    },
    {
      element: (
        <RequireAuth
          allowedRoles={[
            ROLE.ROLE_ADMIN,
            ROLE.ROLE_MAPAQ,
            ROLE.ROLE_MAPAQ_RO,
            ROLE.ROLE_SUPPORT,
            ROLE.ROLE_SUPPORT_RO,
          ]}
        >
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'admin_veterinarian', element: <DashboardAdminVeterinarian /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ASSOCIATION]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'organization', element: <DashboardOrganization /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_DETENTEUR_PERMIS]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'permitholder', element: <Dashboard /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_PRODUCER]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [
        { path: 'producer', element: <DashboardProducer /> },
        { path: 'purchase_summary', element: <ProducerPurchaseSummaryPage /> },
      ],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_VET]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'veterinarian', element: <DashboardVeterinarian /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_VET]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [
        {
          path: ROUTE.veterinarian.dashboard.pathProducerShort,
          element: <DashboardVeterinarianProducer />,
        },
      ],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_VET]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [
        {
          path: ROUTE.veterinarian.dashboard.pathProducerPurchaseSummaryShort,
          element: <DashboardVeterinarianPurchaseSummaryPage />,
        },
      ],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_VET]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [
        {
          path: ROUTE.veterinarian.dashboard.pathPrescriptionSummary,
          element: <VeterinarianPrescriptionsSummaryPage />,
        },
      ],
    },
  ],
};

const reportRoutes = {
  path: 'report',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'download', element: <ReportPage /> },
  ],
};

const rndDashboardRoutes = {
  path: 'dashboard_rnd',
  element: <DashboardNavigate />,
  children: [
    { path: '', element: <Navigate to="/dashboard_rnd" /> },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'organization', element: <RndDashboardOrganization /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'permit', element: <RndDashboardPermitholder /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'producer', element: <RndDashboardProducer /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'veterinarian', element: <RndDashboardVeterinarian /> }],
    },
  ],
};

const userDashboardRoutes = {
  path: 'dashboard_user',
  element: <DashboardNavigate />,
  children: [
    { path: '', element: <Navigate to="/dashboard_user" /> },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'organization', element: <UserDashboardOrganization /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'permit', element: <UserDashboardPermitholder /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'producer', element: <UserDashboardProducer /> }],
    },
    {
      element: (
        <RequireAuth allowedRoles={[ROLE.ROLE_ADMIN, ROLE.ROLE_SUPPORT, ROLE.ROLE_SUPPORT_RO]}>
          <Outlet />
        </RequireAuth>
      ),
      children: [{ path: 'veterinarian', element: <UserDashboardVeterinarian /> }],
    },
  ],
};

// ENTITY ROUTES
const accessApiKeyRoutes = {
  path: 'access-api-key',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: 'list', element: <AccessApiKeyListPage /> },
    { path: '*', element: <Navigate to="/404" /> },
  ],
};

const systemRoutes = {
  path: 'system',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: 'admin', element: <SystemPageAdmin /> },
    { path: '*', element: <Navigate to="/404" /> },
  ],
};

const activeIngredientRoutes = {
  path: 'active_ingredient',
  element: (
    <RequireAuth checkApprovedPermit>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <ActiveIngredientListPage /> },
  ],
};

const animalCategoryRoutes = {
  path: 'animal_category',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <AnimalCategoryListPage /> },
  ],
};

const declarationRoutesRoleAdmin = {
  path: 'declaration',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <DeclarationListPage /> },
    { path: ':id/edit', element: <DeclarationEditPage /> },
    { path: ':id/duplicate', element: <DeclarationDuplicatePage /> },
  ],
};

const declarationTemplateRoutesRoleAdmin = {
  path: 'declaration_template',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <DeclarationTemplateListPage /> },
  ],
};

const declarationTemplateLotRoutes = {
  path: 'declaration_template_lot',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <DeclarationTemplateLotListPage /> },
  ],
};

const declarationRoutesRoleUser = {
  ...declarationRoutesRoleAdmin,
  children: [
    ...declarationRoutesRoleAdmin.children,
    {
      path: 'add',
      element: <DeclarationMultistepCreatePage />,
    },
    {
      path: 'duplicate',
      element: <DeclarationMultistepDuplicatePage />,
    },
    {
      path: 'from_template/:templateId/add',
      element: <DeclarationCreatePage />,
    },
    {
      path: 'from_template_lot/:templateLotId/add',
      element: <DeclarationCreateFromTemplateLotPage />,
    },
  ],
};

const declarationSiteUpdateRoutes = {
  path: ROUTE.declaration.pathSiteUpdate,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <DeclarationSiteUpdateListPage /> },
  ],
};

const finalAddressRoutes = {
  path: ROUTE.finalAddress.path,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <FinalAddressListPage /> },
  ],
};

const ingredientFamilyRoutes = {
  path: ROUTE.ingredientFamily.path,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: 'list', element: <IngredientFamilyList /> },
    { path: '*', element: <Navigate to="/404" /> },
  ],
};

const medicationRoutes = {
  path: ROUTE.medication.path,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: 'list', element: <MedicationListPage /> },
    { path: ':id', element: <MedicationOverviewPage /> },
    { path: '*', element: <Navigate to="/404" /> },
  ],
};

const medicationTypeRoutes = {
  path: 'medication_type',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <MedicationTypeListPage /> },
  ],
};

const omvqEstablishmentRoutes = {
  path: 'omvq_establishment',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <OmvqEstablishmentListPage /> },
  ],
};

const omvqEstablishmentCodeRoutes = {
  path: 'omvq_establishment_code',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <OmvqEstablishmentCodeListPage /> },
  ],
};

const omvqVeterinarianRoutes = {
  path: 'omvq_veterinarian',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <OmvqVeterinarianListPage /> },
  ],
};

const organizationRoutes = {
  path: 'organization',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <OrganizationListPage /> },
    { path: ROUTE.epqAccessRequest.path, element: <ProducerAccessRequestList /> },
  ],
};

const pendingDeclarationRoutes = {
  path: 'pending_declaration',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <PendingDeclarationPage /> },
    { path: ':id/edit', element: <PendingDeclarationEdit /> },
  ],
};

const permitRoutesRoleAdmin = {
  path: ROUTE.permit.path,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <PermitList /> },
    { path: ':id', element: <PermitView /> },
  ],
};

const permitRoutesRoleUser = { ...permitRoutesRoleAdmin };

const permitCodeRoutes = {
  path: 'permit_code',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <PermitCodeLister /> },
    { path: 'add', element: <PermitCodeAdd /> },
    { path: ':id/edit', element: <PermitCodeEdit /> },
  ],
};

const producerRoutes = {
  path: 'producer',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: ROUTE.epqAccessRequest.path, element: <ProducerAccessRequestList /> },
    {
      path: `${ROUTE.epqAccessRequest.path}/:id/users`,
      element: <ProducerRequestUserAccessList />,
    },
    {
      path: `${ROUTE.epqAccessRequest.formPath}`,
      element: <ProducerRequestAccessFormPage />,
    },
    {
      path: `${ROUTE.epqAccessRequest.path}/:id/users/:userId/detail`,
      element: <ProducerRequestUserAccessDetailList />,
    },
    { path: '*', element: <Navigate to="/404" /> },
  ],
};

const productRoutes = {
  path: 'product',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <ProductListPage /> },
  ],
};

const productTypeRoutes = {
  path: 'product_type',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <ProductTypeLister /> },
  ],
};

const productionStageRoutes = {
  path: 'production_stage',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <ProductionStagesListPage /> },
  ],
};

const productionStageGroupRoutes = {
  path: 'production_stage_group',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <ProductionStagesGroupListPage /> },
  ],
};

const regulationDateLimitRoutes = {
  path: ROUTE.regulationDateLimit.path,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <RegulationDateLimitListPage /> },
  ],
};

const routeOfAdministrationRoutes = {
  path: ROUTE.routeOfAdministration.path,
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: 'list', element: <RouteOfAdministrationList /> },
    { path: '*', element: <Navigate to="/404" /> },
  ],
};

const speciesRoutes = {
  path: 'species',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <SpeciesListPage /> },
  ],
};

const userApiKeyAdminRoutes = {
  path: 'user_api_key',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '', element: <UserApiKeyAdminListPage /> },
    { path: '*', element: <Navigate to="/404" /> },
    {
      path: 'permit_holder',
      element: <UserApiKeyAdminListPage baseFilter="&userAccountRoles=ROLE_DETENTEUR_PERMIS" />,
    },
    { path: 'vet', element: <UserApiKeyAdminListPage baseFilter="&userAccountRoles=ROLE_VET" /> },
  ],
};

const userApiKeyCreatorsRoutes = {
  path: 'user_api_key_creator',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <UserApiKeyLister /> },
    { path: 'add', element: <PermitCodeAdd /> },
    { path: ':id/edit', element: <PermitCodeEdit /> },
  ],
};

const useTermRoutes = {
  path: 'use_term',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <UseTermListPage /> },
  ],
};

const vetDeclarationRoutes = {
  path: 'vet_declaration',
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  children: [
    { path: '*', element: <Navigate to="/404" /> },
    { path: 'list', element: <VetDeclarationListPage /> },
  ],
};

const roleUserRoutes = [
  mainRoutes,
  dashboardRoutes,
  productRoutes,
  medicationRoutes,
  activeIngredientRoutes,
  productionStageRoutes,
  permitRoutesRoleUser,
  permitCodeRoutes,
  declarationRoutesRoleUser,
  declarationTemplateLotRoutes,
  pendingDeclarationRoutes,
  declarationSiteUpdateRoutes,
  userApiKeyCreatorsRoutes,
  userAccountCreationRoutes,
  userProfileRoute,
  useTermRoutes,
  omvqEstablishmentCodeRoutes,
  vetDeclarationRoutes,
  animalCategoryRoutes,
  producerRoutes,
  docsRoutes,
  organizationRoutes,
  ingredientFamilyRoutes,
  routeOfAdministrationRoutes,
];

const roleAdminRoutes = [
  mainRoutes,
  dashboardRoutes,
  userDashboardRoutes,
  rndDashboardRoutes,
  productTypeRoutes,
  productRoutes,
  medicationTypeRoutes,
  medicationRoutes,
  activeIngredientRoutes,
  speciesRoutes,
  animalCategoryRoutes,
  productionStageRoutes,
  productionStageGroupRoutes,
  userAccountCreationRoutes,
  userAccountRoutes,
  permitRoutesRoleAdmin,
  permitCodeRoutes,
  declarationRoutesRoleAdmin,
  userApiKeyAdminRoutes,
  userProfileRoute,
  omvqVeterinarianRoutes,
  producerRoutes,
  omvqEstablishmentRoutes,
  useTermRoutes,
  regulationDateLimitRoutes,
  omvqEstablishmentCodeRoutes,
  debugRoute,
  vetDeclarationRoutes,
  docsRoutes,
  organizationRoutes,
  accessApiKeyRoutes,
  systemRoutes,
  reportRoutes,
  finalAddressRoutes,
  ingredientFamilyRoutes,
  routeOfAdministrationRoutes,
];

const offLineRoutes = {
  path: '/',
  children: [
    { path: '*', element: <Navigate to="/" /> },
    { path: '/', element: <HomePage /> },
    { path: '/home', element: <HomePage /> },
    { path: '/login', element: <LoginPage /> },
    { path: '/login_callback', element: <LoginPageCallBack /> },
    { path: 'logout', element: <LogoutPage /> },
    { path: '/logout_callback', element: <Navigate to="/login" /> },
    { path: '/signup', element: <SignupProfilePage /> },
    { path: '/404', element: <NotFoundPage /> },
    { path: '/500', element: <FatalErrorPage /> },
    debugHomeProdRoute,
    docsRoutes,
    userAccountCreationRoutes,
    userProfileRoute,
  ],
};

export function useCustomRoutes() {
  const {
    user,
    loggedIn,
    hasUser,
    isRoleMapaqRoOrAbove,
    isRoleAssociationOrAbove,
    isRoleSupportRoOrAbove,
  } = useAuth();

  let routes: any[] = [];
  if (hasUser) {
    routes = routes.concat(
      isRoleMapaqRoOrAbove(user?.role as ROLE) ||
        isRoleAssociationOrAbove(user?.role as ROLE) ||
        isRoleSupportRoOrAbove(user?.role as ROLE)
        ? roleAdminRoutes
        : roleUserRoutes
    );
  } else {
    routes = routes.concat([offLineRoutes]);
  }

  return useRoutes(routes);
}

export { mainRoutes, userAccountRoutes, offLineRoutes, roleUserRoutes, roleAdminRoutes };
